/**
 * Client-side analytics utility for PostHog.
 * Use only in browser; all functions no-op when PostHog is disabled or consent not given.
 *
 * Conversion funnel: Landing → Search → Event Detail → External Link
 * @see FRE-233
 */

/** Minimal type for PostHog client (avoids importing posthog-js types in this module). */
interface PostHogClient {
  capture: (event: string, properties?: Record<string, unknown>) => void;
  /**
   * Capture an exception as a PostHog `$exception` event (error tracking).
   * Complements the `capture_exceptions` init option, which autocaptures
   * *unhandled* errors + promise rejections but never fires for errors that a
   * React error boundary already caught — those must be forwarded here.
   *
   * Optional for the same reason as {@link isFeatureEnabled} above: the
   * lightweight test stubs of this client don't implement it; production
   * posthog-js always does.
   */
  captureException?: (error: unknown, properties?: Record<string, unknown>) => void;
  opt_in_capturing: () => void;
  opt_out_capturing: () => void;
  people: { set: (props: AnalyticsUserProperties) => void };
  /**
   * Feature-flag evaluation (WEB-448). Optional because the lightweight test
   * stubs of this client don't implement it; production posthog-js always
   * does. Returns `undefined` when flags have not loaded yet.
   */
  isFeatureEnabled?: (key: string) => boolean | undefined;
  /**
   * Subscribe to feature-flag (re)loads; returns an unsubscribe fn. Used by
   * `useMusicConnectionsEnabled` so gated surfaces re-render when a flag flips
   * from dark → on without a code change.
   */
  onFeatureFlags?: (callback: (flags: string[]) => void) => () => void;
  /**
   * JSON payload attached to a matched feature flag (FRE-1231). Optional for
   * the same reason as {@link isFeatureEnabled}: lightweight test stubs don't
   * implement it; production posthog-js always does. Returns `undefined` when
   * flags have not loaded or the flag carries no payload.
   */
  getFeatureFlagPayload?: (key: string) => unknown;
  /**
   * Alias the current device to a person distinct_id (FRE-1261). Optional for
   * the same reason as {@link isFeatureEnabled}: lightweight test stubs don't
   * implement it; production posthog-js always does.
   */
  identify?: (distinctId: string) => void;
  /**
   * Clear the identified distinct_id and start a fresh anonymous one (called
   * on logout). Optional — see {@link identify}.
   */
  reset?: () => void;
  /**
   * Session replay controls (WEB-1204). Optional because lightweight test
   * stubs don't implement them; production posthog-js always does.
   * `stopSessionRecording` sets `disable_session_recording` until
   * `startSessionRecording` clears it.
   */
  stopSessionRecording?: () => void;
  startSessionRecording?: () => void;
}

/** localStorage key holding the analytics consent decision. Exported for tests. */
export const CONSENT_STORAGE_KEY = 'loci_analytics_consent';
const CONSENT_GRANTED = 'granted';
const CONSENT_DENIED = 'denied';

/**
 * Browser CustomEvent name fired when the user's analytics consent decision
 * changes (cookie banner Accept / Decline, or Settings → Privacy toggle).
 *
 * `AnalyticsProvider` listens for this so PostHog can initialize *late* — i.e.
 * only after the user grants consent. Before consent, no PostHog code is
 * loaded and no `/decide` / `/array.js` requests fire (FRE-809).
 */
export const ANALYTICS_CONSENT_CHANGED_EVENT = 'loci:analytics-consent-changed';

/**
 * Browser CustomEvent name fired when {@link registerPostHog} stores a
 * non-null client (FRE-1269).
 *
 * Flag hooks (`useFlagEnabled` / `useFlagPayload`) listen so a late
 * `posthog.init()` — the common path after an already-granted consent load —
 * re-runs their read+subscribe without needing a consent-change event.
 */
export const ANALYTICS_POSTHOG_READY_EVENT = 'loci:analytics-posthog-ready';

/** Payload shape for the {@link ANALYTICS_CONSENT_CHANGED_EVENT} event. */
export interface AnalyticsConsentChangedDetail {
  granted: boolean;
}

/** Funnel step events for conversion tracking */
export const ANALYTICS_EVENTS = {
  /** User landed on site (homepage) */
  LANDING_VIEW: 'landing_view',
  /** User viewed an event detail page */
  EVENT_DETAIL_VIEW: 'event_detail_view',
  /** User clicked through to external ticketing (conversion) */
  EXTERNAL_LINK_CLICK: 'external_link_click',
  /**
   * Nearby recommendations card mounted with at least one populated tab
   * (FRE-1299 / R9). Fires once per event-detail card view.
   */
  NEARBY_CARD_VIEWED: 'nearby_card_viewed',
  /**
   * Outbound click on a Nearby card item link (FRE-1299 / R9). Carries tab,
   * supply source, and editor-pick so curated-vs-Places CTR can be compared.
   */
  NEARBY_ITEM_CLICKED: 'nearby_item_clicked',
  /** User confirmed ticket purchase handoff to affiliate partner (FRE-724) */
  TICKET_HANDOFF_CONTINUE: 'ticket_handoff_continue',
  /** Generic page view (also captured by PostHog automatically with capture_pageview) */
  PAGE_VIEW: '$pageview',
  /** Auth Home feed mounted on the client (WEB-234) */
  HOME_FEED_VIEWED: 'home_feed_viewed',
  /** Top match featured CTA or save action clicked on auth Home (WEB-234) */
  TOP_MATCH_CLICKED: 'top_match_clicked',
  /** Rail card clicked inside any Home rail (WEB-234) */
  RAIL_CARD_CLICKED: 'rail_card_clicked',
  /** "SEE ALL →" link clicked on a Home rail (WEB-234) */
  RAIL_SEE_ALL_CLICKED: 'rail_see_all_clicked',
  /** Editorial callout link clicked on auth Home (WEB-234) */
  EDITORIAL_CLICKED: 'editorial_clicked',
  /** Apple Music onboarding connect step mounted (WEB-444) */
  APPLE_MUSIC_STEP_SHOWN: 'apple_music_step_shown',
  /** User initiated the Apple Music connect flow (consent confirmed) (WEB-444) */
  APPLE_MUSIC_CONNECT_STARTED: 'apple_music_connect_started',
  /** Apple Music connect succeeded (WEB-444) */
  APPLE_MUSIC_CONNECTED: 'apple_music_connected',
  /** User skipped the Apple Music onboarding connect step (WEB-444) */
  APPLE_MUSIC_STEP_SKIPPED: 'apple_music_step_skipped',
  /**
   * Music-connection launch funnel (WEB-448). Superset of the WEB-444
   * `apple_music_*` events: entry-point discriminated (`onboarding` |
   * `settings`) and covers the full authorize lifecycle plus the WEB-446
   * edge states. Emitted from `lib/telemetry/musicConnectionEvents.ts` — do
   * not `track()` these names directly.
   */
  MUSIC_CONNECT_SHOWN: 'music_connect_shown',
  MUSIC_CONNECT_STARTED: 'music_connect_started',
  MUSIC_AUTHORIZE_SUCCESS: 'music_authorize_success',
  MUSIC_AUTHORIZE_FAILURE: 'music_authorize_failure',
  MUSIC_CONNECTED: 'music_connected',
  MUSIC_CONNECT_SKIPPED: 'music_connect_skipped',
  MUSIC_AUTHORIZE_TIMEOUT: 'music_authorize_timeout',
  MUSIC_AUTHORIZE_CANCELLED: 'music_authorize_cancelled',
  MUSIC_CONNECTION_ERROR: 'music_connection_error',
  MUSIC_RETRY_ATTEMPTED: 'music_retry_attempted',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** User properties we set (location, device type) */
export interface AnalyticsUserProperties {
  device_type?: 'mobile' | 'tablet' | 'desktop';
  location_country?: string;
  location_region?: string;
  /**
   * Cohort key (WEB-448): whether the user has an active Apple Music
   * connection. Set via {@link setMusicConnectionStatus}; once set it attaches
   * to every subsequent event so save/RSVP rate can be split by connection
   * state (the north-star lift metric). Snake_case to match the PostHog
   * person-property shape, consistent with the other keys here.
   */
  has_music_connection?: boolean;
}

/** PostHog client reference set by AnalyticsProvider after init. */
let posthogClient: PostHogClient | null = null;

/**
 * Max captures held while posthog-js is still lazy-loading (WEB-1182). The
 * window is one dynamic import + `init()`, so a real session buffers a handful
 * (entry `$pageview`, `landing_view`, a mount-time funnel event); the cap only
 * bounds a runaway caller.
 */
export const PRE_INIT_CAPTURE_LIMIT = 50;

/** A `track()` / `capturePageView()` call made before the PostHog client existed. */
interface PendingCapture {
  eventName: string;
  properties?: Record<string, unknown>;
}

/**
 * Captures issued after consent was granted but before {@link registerPostHog}
 * stored a client (WEB-1182). Init is lazy (`import('posthog-js')`), so without
 * this the entry `$pageview` and every mount-time `track()` fell on a null
 * client and were silently dropped. Only consent-cleared events are ever
 * queued, and the queue is emptied on decline / teardown / flush.
 */
let pendingCaptures: PendingCapture[] = [];

/**
 * Buffer a capture until the client registers. **Drops the newest** once
 * {@link PRE_INIT_CAPTURE_LIMIT} is reached: the entry `$pageview` is the first
 * thing queued and the reason this buffer exists, so oldest-first eviction
 * would discard exactly the event under repair. Returns `false` when the
 * capture was dropped because the buffer is full.
 */
function queuePendingCapture(eventName: string, properties?: Record<string, unknown>): boolean {
  if (pendingCaptures.length >= PRE_INIT_CAPTURE_LIMIT) return false;
  pendingCaptures.push({ eventName, properties });
  return true;
}

/**
 * Send one consent-cleared capture now if a client exists, otherwise buffer it.
 * Callers have already passed {@link shouldTrack}. Returns `false` only when
 * the capture was dropped by a full pre-init buffer.
 */
function captureOrQueue(eventName: string, properties?: Record<string, unknown>): boolean {
  const client = getPostHog();
  if (!client) return queuePendingCapture(eventName, properties);
  try {
    client.capture(eventName, properties);
  } catch {
    // analytics must not break the app
  }
  return true;
}

/**
 * Replay buffered captures to a freshly registered client, in call order. The
 * queue is swapped out first so a re-entrant `track()` from inside `capture`
 * goes straight to the client instead of looping. Consent is re-checked here so
 * a decision revoked since queueing (another tab, a missed clear) can never
 * leak an event.
 */
function flushPendingCaptures(client: PostHogClient): void {
  const queued = pendingCaptures;
  pendingCaptures = [];
  if (queued.length === 0 || !shouldTrack()) return;
  for (const { eventName, properties } of queued) {
    try {
      // Re-strip on replay: the buffer holds the caller's object, which may
      // have gained a free-text query key since `track()` ran (DAT-2).
      client.capture(eventName, stripFreeTextQueryProperties(properties));
    } catch {
      // analytics must not break the app
    }
  }
}

/**
 * Register the PostHog client after init. Called by AnalyticsProvider.
 *
 * When `client` is non-null, first replays any captures buffered while the
 * client was still loading (WEB-1182), then dispatches
 * {@link ANALYTICS_POSTHOG_READY_EVENT} so flag hooks that mounted before init
 * can re-subscribe (FRE-1269). Clearing with `null` is silent — teardown must
 * not wake subscribers — and discards the buffer.
 *
 * Opt the client in ({@link applyAnalyticsOptIn}) **before** registering it:
 * posthog-js drops `capture()` while opted out, and the replay and any
 * ready-listener capture happen here.
 */
export function registerPostHog(client: PostHogClient | null): void {
  posthogClient = client;
  if (client === null) {
    pendingCaptures = [];
    return;
  }
  flushPendingCaptures(client);
  if (typeof CustomEvent !== 'undefined' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ANALYTICS_POSTHOG_READY_EVENT));
  }
}

/**
 * Get the current PostHog client. Returns null if not initialized.
 */
export function getPostHog(): PostHogClient | null {
  return posthogClient;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/** Playwright-only opt-in. Absent unless that spec's init script sets it. */
interface E2EAnalyticsOverride {
  key?: string;
  host?: string;
}

/**
 * Key and host for the auth-token scrub spec.
 *
 * The shared Playwright server does not set `NEXT_PUBLIC_ENABLE_ANALYTICS` or
 * a PostHog key: that flag also decides whether `CookieConsent` renders, and
 * a key on a production `next start` turns analytics on for every spec.
 * `NEXT_PUBLIC_E2E_HOOKS` is already the e2e-build gate. A spec that needs a
 * capture sets `window.__LOCI_E2E_ANALYTICS__` before the app boots. Any
 * other page leaves the property unset, so this returns null.
 */
export function e2eAnalyticsOverride(): { key: string; host: string } | null {
  if (process.env.NEXT_PUBLIC_E2E_HOOKS !== 'true') return null;
  if (!isBrowser()) return null;
  const raw = (window as Window & { __LOCI_E2E_ANALYTICS__?: E2EAnalyticsOverride })
    .__LOCI_E2E_ANALYTICS__;
  if (!raw || typeof raw.key !== 'string' || raw.key.length < 10) return null;
  if (typeof raw.host !== 'string' || raw.host.length === 0) return null;
  return { key: raw.key, host: raw.host };
}

/**
 * Whether analytics is enabled by environment (key set, production/preview or explicitly enabled).
 * Does not check consent; consent is enforced in track/capturePageView.
 */
export function isAnalyticsEnabledByEnv(): boolean {
  if (!isBrowser()) return false;
  if (e2eAnalyticsOverride()) return true;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key || key.length < 10) return false;
  const nodeEnv = process.env.NODE_ENV;
  const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV;
  if (nodeEnv === 'production' || vercelEnv === 'production' || vercelEnv === 'preview')
    return true;
  const enabled = process.env.NEXT_PUBLIC_ENABLE_ANALYTICS;
  return String(enabled).toLowerCase() === 'true';
}

/**
 * Get stored consent. Returns null if not set (no decision yet).
 */
export function getConsent(): 'granted' | 'denied' | null {
  if (!isBrowser()) return null;
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === CONSENT_GRANTED) return 'granted';
    if (value === CONSENT_DENIED) return 'denied';
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist consent and opt in/out of PostHog capturing.
 *
 * On grant: if PostHog has already initialized, opt in. If not (the common
 * case — see {@link AnalyticsProvider}, which defers `posthog.init()` until
 * consent is granted), the {@link ANALYTICS_CONSENT_CHANGED_EVENT} fired below
 * triggers a late init.
 *
 * On deny: opt out if initialized and discard any captures buffered for a
 * client that has not loaded yet (WEB-1182); the event still fires so
 * listeners (e.g. a future settings toggle) can react.
 *
 * Essential cookies (`accessToken` / `refreshToken` / `loci_session` / CSRF)
 * are not touched here — they live on httpOnly server-set cookies that this
 * client-only function has no API to reach.
 */
export function setConsent(granted: boolean): void {
  if (!isBrowser()) return;
  // Before the storage write, so a blocked/throwing localStorage can't leave
  // buffered events behind a decline.
  if (!granted) pendingCaptures = [];
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, granted ? CONSENT_GRANTED : CONSENT_DENIED);
    const client = getPostHog();
    if (client) {
      if (granted) {
        applyAnalyticsOptIn(client);
      } else {
        client.opt_out_capturing();
      }
    }
    // Notify AnalyticsProvider (and any other listeners) so PostHog can
    // initialize lazily on the first `granted` after page load. Wrapped in
    // a guard because `CustomEvent` is undefined in some non-browser test
    // environments — analytics must never break the app.
    if (typeof CustomEvent !== 'undefined' && typeof window !== 'undefined') {
      const detail: AnalyticsConsentChangedDetail = { granted };
      window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_CHANGED_EVENT, { detail }));
    }
  } catch {
    // ignore
  }
}

/**
 * Apply Settings → Privacy "Usage analytics" as the authoritative
 * bidirectional consent control (FRE-1271 / FRE-1272).
 *
 * - ON → {@link setConsent}(true): writes `loci_analytics_consent='granted'`,
 *   dispatches {@link ANALYTICS_CONSENT_CHANGED_EVENT}, and either opts in an
 *   already-initialized client or triggers late `posthog.init()` via
 *   `AnalyticsProvider`.
 * - OFF → {@link setConsent}(false): writes `'denied'` and opts out when a
 *   client exists.
 *
 * Previously this early-returned when PostHog was not yet initialized (the
 * common path after cookie-banner Decline), which left consent stuck at
 * `denied` with zero recovery — the Privacy toggle could never re-grant.
 */
export function syncProductAnalyticsFromPrivacyPreference(analyticsEnabled: boolean): void {
  setConsent(analyticsEnabled);
}

/**
 * The single "PostHog is (re-)opted in" transition. Every grant path —
 * post-init (`AnalyticsProvider`), cookie-banner accept (`setConsent`), and
 * Settings → Privacy re-enable (`syncProductAnalyticsFromPrivacyPreference`) —
 * runs this same ritual so readiness side effects can't drift between them:
 * opt in, record baseline person properties, apply any retained identify
 * (FRE-1261).
 *
 * Takes the client explicitly and works on one that is not registered yet, so
 * `AnalyticsProvider` can opt in *before* {@link registerPostHog} (WEB-1182):
 * registering replays the pre-init capture buffer and announces readiness, and
 * posthog-js drops `capture()` while opted out. An opt-out persisted by an
 * earlier decline lasts until `opt_in_capturing()`, so anything replayed first
 * would be silently lost.
 */
export function applyAnalyticsOptIn(client: PostHogClient): void {
  client.opt_in_capturing();
  // Set user properties after opt-in so they are recorded (init may run before consent).
  client.people.set({ device_type: getDeviceType() });
  flushPendingAnalyticsIdentity(client);
}

/**
 * The user id the current session should be identified as, retained until
 * PostHog is actually ready. Init is lazy AND consent-gated (FRE-809), so auth
 * usually resolves before a client exists; {@link flushPendingAnalyticsIdentity}
 * applies the retained id on init / consent grant.
 */
let pendingIdentifyUserId: string | null = null;

/**
 * The id most recently sent to `posthog.identify()`, so repeat flush triggers
 * (post-init, consent grant, every privacy-preference sync) skip the call
 * instead of leaning on posthog-js's internal same-distinct_id short-circuit.
 * Cleared by {@link resetAnalyticsIdentity}.
 */
let lastIdentifiedUserId: string | null = null;

/**
 * Identify the signed-in user to PostHog (FRE-1261).
 *
 * Without this every browser evaluates feature flags under its anonymous
 * device UUID — "10% of users" really means "10% of devices", and
 * person-targeted release conditions can never match signed-in users.
 * `identify(user.id)` aliases the device to the person so flag bucketing and
 * event attribution follow the account across devices.
 *
 * Consent-gated like every other call here: before consent there is no client,
 * so the id is retained and applied when init eventually happens.
 */
export function identifyUser(userId: string): void {
  if (!isBrowser() || !userId) return;
  pendingIdentifyUserId = userId;
  flushPendingAnalyticsIdentity();
}

/**
 * Apply a retained {@link identifyUser} id once PostHog is ready. Called after
 * init + opt-in by `AnalyticsProvider` and on consent grant. Safe to call
 * repeatedly — posthog-js treats `identify()` with an unchanged distinct_id as
 * a no-op. `client` defaults to the registered one; {@link applyAnalyticsOptIn}
 * passes its own because it can run before registration (WEB-1182).
 */
export function flushPendingAnalyticsIdentity(client: PostHogClient | null = getPostHog()): void {
  if (pendingIdentifyUserId === null) return;
  if (pendingIdentifyUserId === lastIdentifiedUserId) return;
  if (!shouldTrack()) return;
  if (!client?.identify) return;
  try {
    client.identify(pendingIdentifyUserId);
    lastIdentifiedUserId = pendingIdentifyUserId;
  } catch {
    // analytics must not break the app
  }
}

/**
 * Drop the identified person on logout (FRE-1261). `posthog.reset()` clears
 * the identified distinct_id and starts a fresh anonymous one, so the next
 * visitor on a shared device can't inherit the previous account's identity,
 * events, or feature-flag buckets. Not gated on {@link shouldTrack} — resetting
 * is privacy-protective, and it no-ops anyway when PostHog never initialized.
 */
export function resetAnalyticsIdentity(): void {
  pendingIdentifyUserId = null;
  lastIdentifiedUserId = null;
  const client = getPostHog();
  if (!client?.reset) return;
  try {
    client.reset();
  } catch {
    // analytics must not break the app
  }
}

/**
 * Whether we should send events (env enabled + consent granted, or no consent required yet).
 * When consent is not yet set, we do not track (GDPR/CCPA: assume no consent until user accepts).
 */
function shouldTrack(): boolean {
  if (!isBrowser()) return false;
  if (!isAnalyticsEnabledByEnv()) return false;
  const consent = getConsent();
  // Require explicit consent to track (GDPR/CCPA)
  return consent === 'granted';
}

/**
 * Detect device type from user agent.
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (!isBrowser() || !navigator?.userAgent) return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk|(android(?!.*mobile))/.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|blackberry|opera mini|iemobile|wpdesktop/.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Property keys that would carry the free text a user typed into search
 * (DAT-2). Search analytics send only `queryLength` / `resultCount`, on
 * `search_query_typed` (`lib/telemetry/searchEvents.ts`); the raw query is
 * never an event property. Keys are compared after lower-casing and removing
 * `_` / `-`, so `search_query`, `searchQuery` and `Search-Query` all match.
 * The URL `?q=` param is stripped separately by the `before_send` backstop
 * (`lib/analytics/authSecretUrl.ts`, WEB-1229).
 */
export const FREE_TEXT_QUERY_PROPERTY_KEYS: readonly string[] = [
  'q',
  'query',
  'querytext',
  'search',
  'searchquery',
  'searchquerytext',
  'searchstring',
  'searchtext',
  'searchterm',
  'term',
  'keyword',
  'keywords',
];

const FREE_TEXT_QUERY_PROPERTY_KEY_SET: ReadonlySet<string> = new Set(
  FREE_TEXT_QUERY_PROPERTY_KEYS,
);

/** Nesting depth the strip walks; deeper values are dropped rather than sent unchecked. */
const FREE_TEXT_QUERY_STRIP_MAX_DEPTH = 5;

function isFreeTextQueryPropertyKey(key: string): boolean {
  return FREE_TEXT_QUERY_PROPERTY_KEY_SET.has(key.toLowerCase().replace(/[_-]/g, ''));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function stripFreeTextQueryValue(value: unknown, depth: number): unknown {
  if (Array.isArray(value)) {
    if (depth >= FREE_TEXT_QUERY_STRIP_MAX_DEPTH) return undefined;
    let changed = false;
    const next = value.map((item) => {
      const stripped = stripFreeTextQueryValue(item, depth + 1);
      if (stripped !== item) changed = true;
      return stripped;
    });
    return changed ? next : value;
  }
  if (!isPlainObject(value)) return value;
  if (depth >= FREE_TEXT_QUERY_STRIP_MAX_DEPTH) return undefined;
  let changed = false;
  const kept: Record<string, unknown> = {};
  for (const [key, inner] of Object.entries(value)) {
    if (isFreeTextQueryPropertyKey(key)) {
      changed = true;
      continue;
    }
    const stripped = stripFreeTextQueryValue(inner, depth + 1);
    if (stripped !== inner) changed = true;
    kept[key] = stripped;
  }
  return changed ? kept : value;
}

/**
 * Drop free-text query properties (see {@link FREE_TEXT_QUERY_PROPERTY_KEYS})
 * at any depth of plain objects and arrays. Returns the same object when
 * nothing is dropped.
 */
export function stripFreeTextQueryProperties(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!properties) return properties;
  return stripFreeTextQueryValue(properties, 0) as Record<string, unknown>;
}

/**
 * Capture a custom event. No-op if analytics disabled or consent not granted.
 * With consent granted but PostHog still loading, the event is buffered and
 * replayed in order by {@link registerPostHog} (WEB-1182). Free-text query
 * properties are dropped before capture (DAT-2).
 */
export function track(eventName: string, properties?: Record<string, unknown>): void {
  if (!shouldTrack()) return;
  captureOrQueue(eventName, stripFreeTextQueryProperties(properties));
}

/**
 * Report a caught exception to PostHog error tracking as a `$exception` event.
 * No-op when analytics is disabled or consent has not been granted (PostHog is
 * not initialized before consent, so `getPostHog()` is null then).
 *
 * Use at React error boundaries and other catch sites where the error is
 * handled locally and therefore never reaches the SDK's unhandled-exception
 * autocapture (`capture_exceptions` in {@link AnalyticsProvider}).
 */
export function captureException(error: unknown, properties?: Record<string, unknown>): void {
  if (!shouldTrack()) return;
  const client = getPostHog();
  if (!client?.captureException) return;
  try {
    client.captureException(error, properties);
  } catch {
    // error reporting must never break the app
  }
}

/**
 * Capture a page view. Call on route change for SPA-style tracking.
 *
 * Returns `true` when the view was captured **or buffered** for the loading
 * client (WEB-1182), `false` when it was dropped (analytics disabled, consent
 * not granted, or the pre-init buffer full). Callers use that to tell a
 * reported page from one that still needs reporting once PostHog is ready.
 * `$current_url` / `$pathname` are resolved here, at call time, so a buffered
 * view keeps the page it was for.
 */
export function capturePageView(path?: string, url?: string): boolean {
  if (!shouldTrack()) return false;
  return captureOrQueue(ANALYTICS_EVENTS.PAGE_VIEW, {
    $current_url: url ?? (isBrowser() ? window.location.href : undefined),
    $pathname: path ?? (isBrowser() ? window.location.pathname : undefined),
  });
}

/**
 * Set user properties (device type, location). Call after init or when location is known.
 */
export function setUserProperties(props: AnalyticsUserProperties): void {
  if (!shouldTrack()) return;
  const client = getPostHog();
  if (!client) return;
  try {
    client.people.set(props);
  } catch {
    // ignore
  }
}

/**
 * Set the `has_music_connection` cohort person property (WEB-448).
 *
 * Once set, PostHog attaches it to every subsequent event, so downstream
 * insights can split save/RSVP rate by connection state (the north-star lift).
 * Call it on: connection success, disconnect, and auth-state load when an
 * existing connection is detected. Consent-gated and null-safe via
 * {@link setUserProperties}.
 */
export function setMusicConnectionStatus(connected: boolean): void {
  setUserProperties({ has_music_connection: connected });
}

/**
 * Funnel: user viewed landing (home) page.
 */
export function trackLandingView(): void {
  track(ANALYTICS_EVENTS.LANDING_VIEW, { funnel_step: 'landing' });
}

/**
 * Funnel: user viewed an event detail page.
 */
export function trackEventDetailView(eventId: string, eventSlug?: string): void {
  track(ANALYTICS_EVENTS.EVENT_DETAIL_VIEW, {
    funnel_step: 'event_detail',
    event_id: eventId,
    event_slug: eventSlug,
  });
}

/**
 * Funnel: user clicked external link (e.g. get tickets).
 */
export function trackExternalLinkClick(eventId: string, url: string, label?: string): void {
  track(ANALYTICS_EVENTS.EXTERNAL_LINK_CLICK, {
    funnel_step: 'external_link',
    event_id: eventId,
    url,
    link_label: label ?? 'tickets',
  });
}

/** Payload shape for `nearby_card_viewed` (FRE-1299 / R9). */
export interface NearbyCardViewedPayload {
  eventId: string;
}

/** Payload shape for `nearby_item_clicked` (FRE-1299 / R9). */
export interface NearbyItemClickedPayload {
  eventId: string;
  /** Active tab when the item was clicked. */
  tab: 'eat' | 'drink' | 'stay';
  /** Supply provenance — Places vs curated (editor) overlay. */
  source: 'places' | 'curated';
  /** Whether the clicked row was an editorial LOCI PICK. */
  isEditorPick: boolean;
}

/**
 * Fire `nearby_card_viewed` once per card mount when the Nearby card renders
 * with at least one populated tab (FRE-1299). Callers dedupe per-mount
 * themselves (see `NearbyRecommendationsCardView`).
 */
export function trackNearbyCardViewed(payload: NearbyCardViewedPayload): void {
  track(ANALYTICS_EVENTS.NEARBY_CARD_VIEWED, {
    event_id: payload.eventId,
  });
}

/**
 * Fire `nearby_item_clicked` when a Nearby card outbound link is activated
 * (FRE-1299). Carries tab / source / editor-pick so the bet review can
 * attribute click-through to Places vs curated supply.
 */
export function trackNearbyItemClicked(payload: NearbyItemClickedPayload): void {
  track(ANALYTICS_EVENTS.NEARBY_ITEM_CLICKED, {
    event_id: payload.eventId,
    tab: payload.tab,
    source: payload.source,
    is_editor_pick: payload.isEditorPick,
  });
}

/**
 * Funnel: user confirmed a Ticket Handoff modal and is being redirected to the
 * affiliate partner's checkout. Fires on "Continue to {Partner}" click (FRE-724).
 */
export function trackTicketHandoffContinue(
  eventId: string,
  partner: string,
  finalPrice: number | null,
): void {
  track(ANALYTICS_EVENTS.TICKET_HANDOFF_CONTINUE, {
    funnel_step: 'ticket_handoff',
    event_id: eventId,
    partner,
    final_price: finalPrice,
  });
}

/**
 * Track recommendation click event (Phase 2: Interaction Tracking).
 * Correlates user clicks with recommendation impressions for A/B testing.
 *
 * @param eventId - ID of the event clicked
 * @param impressionId - Optional impression ID to correlate with
 * @param variant - Optional A/B test variant (control, test-variant-1, etc.)
 * @see API-1008 (A/B testing implementation)
 */
export function trackRecommendationClick(
  eventId: string,
  impressionId?: string,
  variant?: string,
): void {
  track('recommendation_click', {
    event_id: eventId,
    impression_id: impressionId,
    action_type: 'click',
    ...(variant && { variant }),
  });
}

/**
 * Optional attribution context for a save / RSVP (WEB-448). Threads the
 * originating surface and — for the reason-trust guardrail — the kind of
 * why-line the card rendered, so save/RSVP rate can be segmented by why-line
 * type. All fields optional; camelCase in, snake_case out.
 */
export interface RecommendationActionContext {
  /** Where the action happened, e.g. `event-detail`, `auth-home`, `discover`. */
  surface?: string;
  /** Home-feed rail the card belonged to, when applicable. */
  rail?: HomeFeedRail;
  /** City the action occurred in. */
  cityCode?: string;
  /**
   * The kind of Apple-derived why-line the card showed (e.g.
   * `artist-you-know`, `genre-match`) — powers the reason-trust guardrail.
   * Kept as a free-form string so analytics is decoupled from UI why-line
   * type churn.
   */
  whyLineKind?: string;
}

/**
 * Map a {@link RecommendationActionContext} into its snake_case PostHog
 * payload, omitting absent fields so the event stays clean.
 */
function buildRecommendationContextPayload(
  context?: RecommendationActionContext,
): Record<string, unknown> {
  if (!context) return {};
  const { surface, rail, cityCode, whyLineKind } = context;
  return {
    ...(surface && { surface }),
    ...(rail && { rail }),
    ...(cityCode && { city_code: cityCode }),
    ...(whyLineKind && { why_line_kind: whyLineKind }),
  };
}

/**
 * Shared emitter for the `recommendation_save` / `recommendation_rsvp` events —
 * they differ only by event name + `action_type`, so attribution-field changes
 * live in one place (WEB-448).
 */
function trackRecommendationAction(
  eventName: 'recommendation_save' | 'recommendation_rsvp',
  actionType: 'save' | 'rsvp',
  eventId: string,
  impressionId?: string,
  variant?: string,
  context?: RecommendationActionContext,
): void {
  track(eventName, {
    event_id: eventId,
    impression_id: impressionId,
    action_type: actionType,
    ...(variant && { variant }),
    ...buildRecommendationContextPayload(context),
  });
}

/**
 * Track recommendation save event.
 * Used for measuring save rate in A/B tests, and — with `context` — the
 * WEB-448 north-star (save rate split by `has_music_connection`) and
 * reason-trust guardrail (`why_line_kind`) metrics.
 *
 * @param eventId - ID of the event saved
 * @param impressionId - Optional impression ID to correlate with
 * @param variant - Optional A/B test variant
 * @param context - Optional surface / rail / city / why-line attribution
 */
export function trackRecommendationSave(
  eventId: string,
  impressionId?: string,
  variant?: string,
  context?: RecommendationActionContext,
): void {
  trackRecommendationAction('recommendation_save', 'save', eventId, impressionId, variant, context);
}

/**
 * Track recommendation RSVP event (WEB-448). Mirrors
 * {@link trackRecommendationSave} — same optional attribution context — so
 * RSVP rate can be split by `has_music_connection` and segmented by
 * `why_line_kind` alongside saves.
 *
 * @param eventId - ID of the event RSVP'd to
 * @param impressionId - Optional impression ID to correlate with
 * @param variant - Optional A/B test variant
 * @param context - Optional surface / rail / city / why-line attribution
 */
export function trackRecommendationRsvp(
  eventId: string,
  impressionId?: string,
  variant?: string,
  context?: RecommendationActionContext,
): void {
  trackRecommendationAction('recommendation_rsvp', 'rsvp', eventId, impressionId, variant, context);
}

/**
 * Track recommendation purchase event (conversion).
 * Primary metric for A/B test evaluation.
 *
 * @param eventId - ID of the event purchased
 * @param impressionId - Optional impression ID to correlate with
 * @param variant - Optional A/B test variant
 */
export function trackRecommendationPurchase(
  eventId: string,
  impressionId?: string,
  variant?: string,
): void {
  track('recommendation_purchase', {
    event_id: eventId,
    impression_id: impressionId,
    action_type: 'purchase',
    ...(variant && { variant }),
  });
}

/**
 * Shared label for the auth Home surface. Used as the `surface` payload on
 * every WEB-234 event so funnels can scope to the authenticated feed.
 */
const AUTH_HOME_SURFACE = 'auth-home';

/**
 * Identifier of a Home feed rail. Drives `rail_*` event payloads (WEB-234).
 * FRE-1258 merged the `tonight` + `for-you` rails into the time-tabbed
 * `up-next` organism; the legacy members stay in the union so historical
 * funnels keep type-checking against recorded payloads. FRE-1265 added
 * `hoods` for the place-first "Explore by neighborhood" band (its payloads
 * never carry `matchPct` — the band is personalization-free).
 */
export type HomeFeedRail = 'tonight' | 'for-you' | 'friends' | 'up-next' | 'hoods';

/** Where on `TopMatchFeatured` the click originated (WEB-234). */
export type TopMatchClickPosition = 'cta' | 'save';

/**
 * Payload shape for `home_feed_viewed` (WEB-234). `hasFriendsGoing` was
 * removed in FRE-1259 with the FriendsGoingRail — the event must not report
 * on a section that no longer renders.
 */
export interface HomeFeedViewedPayload {
  cityCode: string;
  hasTonight: boolean;
  hasForYou: boolean;
  topMatchPct: number | null;
}

/** Payload shape for `top_match_clicked` (WEB-234). */
export interface TopMatchClickedPayload {
  cityCode: string;
  eventId: string;
  matchPct: number;
  position: TopMatchClickPosition;
}

/** Base fields shared by every `rail_card_clicked` payload variant. */
interface RailCardClickedBasePayload {
  cityCode: string;
  eventId: string;
  rail: HomeFeedRail;
  /** 0-indexed position of the card within the rail. */
  position: number;
}

/**
 * Payload shape for `rail_card_clicked` (WEB-234). Discriminated union: a
 * single click is accented by at most one of `matchPct` (when the rail
 * scores on taste — `tonight`, `for-you`) or `friendsCount` (when the rail
 * scores on social proof — `friends`). Both-present is a type error.
 *
 * FRE-1257 added the neither-present variant: when the viewer has no real
 * taste signal the card renders without a match badge, and the analytics
 * payload must be equally honest — no `match_pct` at all, never a fabricated
 * default score.
 */
export type RailCardClickedPayload =
  | (RailCardClickedBasePayload & { matchPct: number; friendsCount?: never })
  | (RailCardClickedBasePayload & { friendsCount: number; matchPct?: never })
  | (RailCardClickedBasePayload & { matchPct?: never; friendsCount?: never });

/** Payload shape for `rail_see_all_clicked` (WEB-234). */
export interface RailSeeAllClickedPayload {
  cityCode: string;
  rail: HomeFeedRail;
}

/** Payload shape for `editorial_clicked` (WEB-234). */
export interface EditorialClickedPayload {
  cityCode: string;
  eventLink: string;
}

/**
 * Fire `home_feed_viewed` once per client mount. Mounted by
 * `HomeAnalyticsConnector` on `(consumer)/page.tsx` after the feed template.
 */
export function trackHomeFeedViewed(payload: HomeFeedViewedPayload): void {
  track(ANALYTICS_EVENTS.HOME_FEED_VIEWED, {
    surface: AUTH_HOME_SURFACE,
    city_code: payload.cityCode,
    has_tonight: payload.hasTonight,
    has_for_you: payload.hasForYou,
    top_match_pct: payload.topMatchPct,
  });
}

/**
 * Fire `top_match_clicked` when the Top Match featured CTA or Save action is
 * clicked. `position` distinguishes the primary CTA from the save button.
 */
export function trackTopMatchClicked(payload: TopMatchClickedPayload): void {
  track(ANALYTICS_EVENTS.TOP_MATCH_CLICKED, {
    surface: AUTH_HOME_SURFACE,
    city_code: payload.cityCode,
    event_id: payload.eventId,
    match_pct: payload.matchPct,
    position: payload.position,
  });
}

/**
 * Fire `rail_card_clicked` when a card inside any Home rail is clicked.
 * `position` is 0-indexed within the rail track. At most one of `matchPct`
 * (taste-scored rails) or `friendsCount` (social-proof rails) is present
 * — enforced by the {@link RailCardClickedPayload} discriminated union.
 * Neither present (FRE-1257 honest badge-free cards) emits the base payload
 * only.
 */
export function trackRailCardClicked(payload: RailCardClickedPayload): void {
  const base = {
    surface: AUTH_HOME_SURFACE,
    city_code: payload.cityCode,
    event_id: payload.eventId,
    rail: payload.rail,
    position: payload.position,
  };
  if (typeof payload.matchPct === 'number') {
    track(ANALYTICS_EVENTS.RAIL_CARD_CLICKED, { ...base, match_pct: payload.matchPct });
    return;
  }
  if (typeof payload.friendsCount === 'number') {
    track(ANALYTICS_EVENTS.RAIL_CARD_CLICKED, { ...base, friends_count: payload.friendsCount });
    return;
  }
  track(ANALYTICS_EVENTS.RAIL_CARD_CLICKED, base);
}

/** Fire `rail_see_all_clicked` when the "SEE ALL →" link on a rail is clicked. */
export function trackRailSeeAllClicked(payload: RailSeeAllClickedPayload): void {
  track(ANALYTICS_EVENTS.RAIL_SEE_ALL_CLICKED, {
    surface: AUTH_HOME_SURFACE,
    city_code: payload.cityCode,
    rail: payload.rail,
  });
}

/** Fire `editorial_clicked` when the editorial callout link is clicked. */
export function trackEditorialClicked(payload: EditorialClickedPayload): void {
  track(ANALYTICS_EVENTS.EDITORIAL_CLICKED, {
    surface: AUTH_HOME_SURFACE,
    city_code: payload.cityCode,
    event_link: payload.eventLink,
  });
}

/** Payload shape for `apple_music_step_shown` (WEB-444). */
export interface AppleMusicStepShownPayload {
  surface: 'onboarding';
  /** Step position within the onboarding flow. */
  step: number;
}

/** Payload shape for `apple_music_connect_started` (WEB-444). */
export interface AppleMusicConnectStartedPayload {
  surface: 'onboarding';
}

/** Payload shape for `apple_music_connected` (WEB-444). */
export interface AppleMusicConnectedPayload {
  surface: 'onboarding';
}

/** Payload shape for `apple_music_step_skipped` (WEB-444). */
export interface AppleMusicStepSkippedPayload {
  surface: 'onboarding';
}

/**
 * Fire `apple_music_step_shown` when the onboarding connect step mounts.
 * Callers dedupe per-mount themselves (see `AppleMusicOnboardingAnalytics`).
 */
export function trackAppleMusicStepShown(payload: AppleMusicStepShownPayload): void {
  track(ANALYTICS_EVENTS.APPLE_MUSIC_STEP_SHOWN, { surface: payload.surface, step: payload.step });
}

/** Fire `apple_music_connect_started` when the user confirms consent and the connect begins. */
export function trackAppleMusicConnectStarted(payload: AppleMusicConnectStartedPayload): void {
  track(ANALYTICS_EVENTS.APPLE_MUSIC_CONNECT_STARTED, { surface: payload.surface });
}

/** Fire `apple_music_connected` when the connect flow completes successfully. */
export function trackAppleMusicConnected(payload: AppleMusicConnectedPayload): void {
  track(ANALYTICS_EVENTS.APPLE_MUSIC_CONNECTED, { surface: payload.surface });
}

/** Fire `apple_music_step_skipped` when the user skips the onboarding connect step. */
export function trackAppleMusicStepSkipped(payload: AppleMusicStepSkippedPayload): void {
  track(ANALYTICS_EVENTS.APPLE_MUSIC_STEP_SKIPPED, { surface: payload.surface });
}
